<template>
  <BContainer
    class="mt-2"
    fluid
  >
    <!-- Filters -->
    <BRow>

      <BCol
        cols="12"
        md="3"
        class="px-1"
      >
        <BFormGroup
          label="Trip Type"
          label-for="filterPendingTripType"
          label-class="font-weight-bolder"
        >
          <BFormSelect
            id="filterPendingTripType"
            v-model="tablePending.filter.trip_type"
            :options="tablePending.options.tripTypes"
            :disabled="tablePending.busy"
          >
            <template #first>
              <BFormSelectOption
                :value="null"
                disabled
              >
                -- select trip type here --
              </BFormSelectOption>
            </template>
          </BFormSelect>
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="5"
        class="px-1"
      >
        <BFormGroup
          label="Vehicle"
          label-for="filterVehicle"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <v-select
            v-model="tablePending.selected.vehicle"
            placeholder="search vehicle here"
            label="vehicle_name"
            class="vs-white"
            clearable
            append-to-body
            input-id="filterVehicle"
            :filter="fuseVehicleFilter"
            :options="(tablePending.options.vehicles)"
            :loading="(tablePending.fetching.vehicles)"
            :disabled="(tablePending.busy || tablePending.fetching.vehicles)"
            :calculate-position="calculateDropPosition"
          >
            <template #option="{ vehicle_plate, vehicle_name }">
              <div class="d-flex flex-column flex-md-row py-1">
                <div class="flex-grow-1">
                  {{ vehicle_name }}
                </div>
                <strong class="min-w-50px">{{ vehicle_plate }}</strong>
              </div>
            </template>
            <template #selected-option="{ vehicle_name, vehicle_plate }">
              <div class="d-flex flex-column flex-md-row">
                <strong class="min-w-75px mr-1">
                  {{ vehicle_plate }}
                </strong>
                <div
                  class="flex-grow-1 pl-1 border-left"
                  style="border-width: 2px !important;"
                >
                  {{ vehicle_name }}
                </div>
              </div>
            </template>
            <template #no-options="">
              no available vehicle
            </template>
          </v-select>
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="4"
        class="px-1"
      >
        <BFormGroup
          label="Delivery Date"
          label-for="filterPendingDeliveryDate"
          label-class="font-weight-bolder"
        >
          <BFormInput
            id="filterPendingDeliveryDate"
            v-model="tablePending.filter.date"
            debounce="1000"
            type="date"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        class="px-1"
      >
        <BFormGroup
          label="Search"
          label-for="filterPendingSearch"
          label-class="font-weight-bolder"
        >
          <BFormInput
            id="filterPendingSearch"
            v-model="tablePending.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        class="px-1"
      >
        <div
          class="d-flex order-md-1 order-2 pb-2"
          style="column-gap: 5px; row-gap: 5px;"
        >
          <BButton
            v-if="selected.details.length <= 0"
            size="sm"
            class="min-w-100px"
            :variant="tablePending.totalRows <= 0 ? 'outline-dark' : 'outline-success'"
            :disabled="tablePending.totalRows <= 0"
            @click.prevent="$event => selectPending(true)"
          >
            Select All
          </BButton>
          <BButton
            v-else-if="selected.details.length > 0"
            size="sm"
            class="min-w-100px"
            variant="outline-warning"
            @click.prevent="$event => selectPending(false)"
          >
            Unselect All
          </BButton>
          <BButton
            size="sm"
            :variant="selected.details.length <= 0 ? 'outline-dark' : 'primary'"
            :disabled="selected.details.length <= 0"
            @click.prevent="$event => showSelectedPending()"
          >
            View Deliveries
          </BButton>
        </div>
      </BCol>
    </BRow>

    <!-- Table -->
    <BRow
      v-if="Number(tabIndex) === 0"
      class="mt-1"
    >
      <BCol
        cols="12"
        class="px-1"
      >
        <BTable
          ref="tablePending"
          small
          hover
          striped
          bordered
          responsive
          show-empty
          :items="tableProvider"
          :busy="tablePending.busy"
          :filter="tablePending.filter"
          :fields="tablePending.fields"
          :stacked="tablePending.stacked"
          :sort-by.sync="tablePending.sortBy"
          :sort-desc.sync="tablePending.sortDesc"
          :sort-direction="tablePending.sortDirection"
          :filter-included-fields="tablePending.filterOn"
          :current-page="tablePending.currentPage"
          :per-page="tablePending.perPage"
        >
          <template #cell(index)="{ index }">
            {{ tableRowNumber(tablePending, index) }}
          </template>

          <template #head(selected)>
            <BFormCheckbox
              v-model="selected.allSelected"
              :indeterminate="selected.indeterminate"
              :disabled="(tablePending.busy || Number(tablePending.totalRows) <= 0)"
              size="lg"
              @change="selectPending"
            />
          </template>

          <template #cell(selected)="{ item }">
            <BFormCheckbox
              v-model="selected.details"
              :value="item.id"
              :disabled="tablePending.busy"
              size="lg"
            />
          </template>

          <template #cell(approvable.schedule_details)="{ value }">
            <div class="text-nowrap">
              <div class="d-flex flex-column flex-md-row">
                <strong class="min-w-75px mr-1">
                  {{ value.vehicle_plate }}
                </strong>
                <div
                  class="flex-grow-1 pl-1 border-left"
                  style="border-width: 2px !important;"
                >
                  {{ value.vehicle_name }}
                </div>
              </div>
            </div>
          </template>

          <template #row-details="row">
            <div class="p-2 bg-white">
              <BTable
                small
                hover
                bordered
                responsive
                show-empty
                class="col-md-6 col-12"
                :stacked="tablePending.stacked"
                :items="row.item.approvable.transfer_details"
                :fields="[
                  { key: 'index', label: '#', class: 'text-center' },
                  { key: 'product_name', label: 'Product Name', class: 'col-md-6 col-12' },
                  { key: 'posting_quantity', label: 'Quantity', class: 'text-center min-w-100px' }
                ]"
              >
                <template #cell(index)="{ index }">
                  <div style="min-width: 25px;">
                    {{ tableRowNumber(tablePending, index) }}
                  </div>
                </template>

                <template #cell(posting_quantity)="{ value }">
                  <div class="text-md-nowrap d-flex justify-content-center align-items-center min-h-35px">
                    <strong
                      class="font-weight-bolder"
                      v-text="numberFormatter(value, '0,0')"
                    />
                  </div>
                </template>
              </BTable>
            </div>
          </template>

          <template #cell(action)="{ item, toggleDetails, detailsShowing }">
            <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center gap-1">
              <BButton
                size="sm"
                variant="primary"
                :disabled="tablePending.busy || selected.details.length > 0"
                @click.prevent="show(item)"
              >
                View
              </BButton>
              <BButton
                size="sm"
                :variant="detailsShowing ? 'danger' : 'warning' "
                :disabled="tablePending.busy"
                @click.prevent="toggleDetails"
              >
                {{ detailsShowing ? 'Hide' : 'Show' }} Products
              </BButton>
            </div>
          </template>

          <template #cell(approvable.transaction_number)="{ value }">
            <b
              class="text-md-nowrap"
              v-text="value"
            />
          </template>

          <template #cell(approvable.transfer_number)="{ value }">
            <b
              class="text-md-nowrap"
              v-text="value"
            />
          </template>

          <template #cell()="{ value }">
            <div
              class="text-md-nowrap"
              v-text="value"
            />
          </template>

          <template #table-busy>
            <div class="text-center py-5">
              <BIcon
                icon="stopwatch"
                font-scale="5"
                animation="cylon"
              />
              <p class="h3 py-2">
                <strong>Loading . . .</strong>
              </p>
            </div>
          </template>

        </BTable>
      </BCol>

      <BCol
        cols="12"
        md="6"
        class="mt-1 mb-2 px-1"
      >
        <BFormSelect
          v-model="tablePending.perPage"
          :options="tablePending.pageOptions"
          :disabled="tablePending.busy"
          class="w-100 w-md-25"
          size="sm"
        />
      </BCol>

      <BCol
        cols="12"
        md="6"
        class="d-flex justify-content-center justify-content-md-end mt-1 px-1"
      >
        <BPagination
          v-model="tablePending.currentPage"
          :total-rows="tablePending.totalRows"
          :per-page="tablePending.perPage"
          :disabled="tablePending.busy"
          aria-controls="table"
          prev-text="Prev"
          next-text="Next"
          first-number
          last-number
          pills
        />
      </BCol>

    </BRow>

    <BModal
      id="modalReceivingMultiple"
      size="xl"
      scrollable
      hide-header
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      modal-class="full-screen"
    >
      <BContainer
        fluid
        style="overflow-y: auto !important;"
      >
        <BRow class="mx-0 mt-1 mb-0">

          <BCol
            cols="12"
            class="px-1 min-h-25px"
          >
            <div
              class="d-flex order-md-1 order-2"
              style="column-gap: 5px; row-gap: 5px;"
            >
              <BButton
                v-if="tablePending.selected.details.length <= 0"
                size="sm"
                class="min-w-100px"
                tabindex="-1"
                :variant="selected.details.length <= 1 ? 'outline-dark' : 'outline-success'"
                :disabled="selected.details.length <= 1"
                @click.prevent="$event => selectReceivings(true)"
              >
                Select All
              </BButton>
              <BButton
                v-else-if="tablePending.selected.details.length > 0"
                size="sm"
                class="min-w-100px"
                variant="outline-success"
                tabindex="-1"
                @click.prevent="$event => selectReceivings(false)"
              >
                Unselect All
              </BButton>
              <BButton
                size="sm"
                tabindex="-1"
                :variant="tablePending.selected.details.length <= 0 ? 'outline-dark' : 'outline-danger'"
                :disabled="tablePending.selected.details.length <= 0"
                @click.prevent="$event => removeReceivings()"
              >
                Remove Selected
              </BButton>
            </div>
          </BCol>

          <BCol
            cols="12"
            class="px-0 px-md-1 mt-1"
          >
            <BTableSimple
              small
              responsive
              sticky-header="calc(100vh - 9rem)"
            >
              <thead>
                <tr>
                  <th class="w-50px text-center">
                    <BFormCheckbox
                      v-model="tablePending.selected.allSelected"
                      :indeterminate="tablePending.selected.indeterminate"
                      :disabled="selected.details.length <= 1"
                      size="lg"
                      @change="selectReceivings"
                    />
                  </th>
                  <th class="w-150px border-left" />
                  <th class="w-250px" />
                  <th class="w-250px text-center border-left bg-light-dark">
                    Category
                  </th>
                  <th class="w-450px border-left bg-light-dark">
                    Product
                  </th>
                  <th class="w-100px text-center border-left bg-light-dark">
                    Quantity
                  </th>
                  <th class="w-50px text-center" />
                </tr>
              </thead>
              <tbody
                v-for="(request, requestKey) in stockRequest.details"
                :key="`details-${requestKey}`"
              >
                <tr
                  v-if="requestKey >= 1"
                >
                  <td
                    colspan="7"
                    variant="secondary"
                    class="text-right"
                  />
                </tr>

                <tr
                  v-for="(product, detailKey) in request.details"
                  :key="`product-${requestKey}-${detailKey}`"
                >
                  <td
                    v-if="(request.details.findIndex(detail => detail.group_id === product.group_id) === detailKey)"
                    :rowspan="(request.details.filter(detail => detail.group_id === product.group_id).length + 3)"
                    style="vertical-align: top !important; text-align: center;"
                    class="border-right"
                  >
                    <div class="text-md-nowrap d-flex justify-content-center align-items-center min-h-35px">
                      <BFormCheckbox
                        v-model="tablePending.selected.details"
                        :disabled="selected.details.length <= 1"
                        :value="request.id"
                        size="lg"
                      />
                    </div>
                  </td>

                  <template v-if="detailKey === 0">
                    <td>
                      Transaction Number
                    </td>
                    <td
                      class="border-left"
                    >
                      <strong class="text-primary">{{ request.transaction_number }}</strong>
                    </td>
                  </template>

                  <template v-else-if="detailKey === 1">
                    <td>
                      Transfer Number
                    </td>
                    <td
                      class="border-left"
                    >
                      <strong>{{ request.transfer_number }}</strong>
                    </td>
                  </template>

                  <template v-else-if="detailKey === 2">
                    <td>
                      Deliver To
                    </td>
                    <td
                      colspan="2"
                      class="border-left"
                    >
                      {{ request.destination_name }}
                    </td>
                  </template>

                  <template v-else-if="detailKey === 3">
                    <td>
                      Delivery From
                    </td>
                    <td
                      colspan="2"
                      class="border-left"
                    >
                      {{ request.origin_name }}
                    </td>
                  </template>

                  <template v-else-if="detailKey === 4">
                    <td>
                      Direct to Customer
                    </td>
                    <td
                      colspan="2"
                      class="border-left"
                    >
                      {{ request.customer }}
                    </td>
                  </template>

                  <template v-else-if="detailKey === 5">
                    <td>
                      Mobile
                    </td>
                    <td
                      colspan="2"
                      class="border-left"
                    >
                      {{ request.mobile_number }}
                    </td>
                  </template>

                  <template v-else-if="detailKey === 6">
                    <td>
                      Address
                    </td>
                    <td
                      colspan="2"
                      class="border-left"
                    >
                      {{ request.address }}
                    </td>
                  </template>

                  <template v-else-if="detailKey === 7">
                    <td>
                      Requester Remarks
                    </td>
                    <td
                      colspan="2"
                      :rowspan="request.details.length - 6"
                      class="border-left"
                      style="vertical-align: top !important;"
                    >
                      {{ request.requester_remarks || 'no remarks' }}
                    </td>
                  </template>

                  <template v-else-if="detailKey >= 8">
                    <td>
                      &nbsp;
                    </td>
                  </template>

                  <td
                    v-if="(request.details.findIndex(detail => String(detail.group_id) === String(product.group_id)) === detailKey)"
                    rowspan="2"
                    style="vertical-align: middle; text-align: center;"
                    class="border-left font-weight-bolder text-primary"
                  >
                    {{ product.category_name }}
                  </td>

                  <td class="border-left">
                    {{ product.product_name }}
                  </td>

                  <td class="text-center border-left">
                    {{ product.quantity }}
                  </td>

                  <td
                    v-if="(request.details.findIndex(detail => detail.group_id === product.group_id) === detailKey)"
                    :rowspan="(request.details.filter(detail => detail.group_id === product.group_id).length + 3)"
                    style="vertical-align: top !important; text-align: center;"
                    class="border-left"
                  >
                    <div class="text-md-nowrap d-flex justify-content-center align-items-center min-h-35px">
                      <BButton
                        class="border-0"
                        :variant="selected.details.length <= 1 ? 'outline-dark' : 'outline-danger'"
                        :disabled="selected.details.length <= 1"
                        @click.prevent="removeReceiving(request, requestKey)"
                      >
                        <BIcon
                          icon="trash"
                          font-scale="1"
                        />
                      </BButton>
                    </div>
                  </td>
                </tr>
                <tr :key="`footer-${requestKey}`">
                  <td />
                  <td
                    class="text-right border-left"
                  >
                    Total Products
                  </td>
                  <td
                    class="text-center border-left border-right"
                  >
                    <b>
                      {{
                        request.details.map(({ quantity }) => quantity).reduce(
                          (total, amount) => Number(total) + Number(amount), 0
                        )
                      }}
                    </b>
                  </td>
                </tr>

                <tr :key="`footer-2-${requestKey}`">
                  <td
                    class="border-left"
                    style="vertical-align: middle !important; text-align: center;"
                  >
                    {{ request.trip_type }} Schedule
                  </td>
                  <td
                    colspan="2"
                    class="px-1 border-left border-right"
                  >
                    <BRow
                      class="px-2"
                    >
                      <BCol
                        cols="12"
                        md="3"
                        class="px-1 px-md-2"
                      >
                        <BFormGroup
                          label="Date"
                          label-for="fvDeliveryDate"
                          class="mb-1"
                        >
                          <strong
                            v-text="request.delivery_date"
                          />
                        </BFormGroup>
                      </BCol>

                      <BCol
                        cols="12"
                        md="3"
                        class="px-1 px-md-2"
                      >
                        <BFormGroup
                          label="Plate Number"
                          label-for="fvVehiclePlate"
                          class="mb-1"
                        >
                          <strong
                            v-text="request.vehicle_plate"
                          />
                        </BFormGroup>
                      </BCol>

                      <BCol
                        cols="12"
                        md="6"
                        class="px-1 px-md-2"
                      >
                        <BFormGroup
                          label="Vehicle"
                          label-for="fvVehicleName"
                          class="mb-1"
                        >
                          <strong
                            v-text="request.vehicle_name"
                          />
                        </BFormGroup>
                      </BCol>

                    </BRow>
                  </td>

                  <td
                    colspan="2"
                    class="px-1 border-left border-right"
                  >
                    <BRow
                      class="px-2"
                    >
                      <BCol
                        cols="12"
                        md="4"
                        class="px-1 px-md-2"
                      >
                        <BFormGroup
                          label="Mobile Number"
                          label-for="fvDriverNumber"
                          class="mb-1"
                        >
                          <strong
                            v-text="request.driver_number"
                          />
                        </BFormGroup>
                      </BCol>

                      <BCol
                        cols="12"
                        md="4"
                        class="px-1 px-md-2"
                      >
                        <BFormGroup
                          label="Driver Name"
                          label-for="fvDriverName"
                          class="mb-1"
                        >
                          <strong
                            v-text="request.driver_name"
                          />
                        </BFormGroup>
                      </BCol>

                      <BCol
                        cols="12"
                        md="4"
                        class="px-1 px-md-2"
                      >
                        <BFormGroup
                          label="Helper"
                          label-for="fvHelper"
                          class="mb-1"
                        >
                          <strong
                            v-text="request.helper"
                          />
                        </BFormGroup>
                      </BCol>

                    </BRow>
                  </td>
                </tr>

                <tr
                  :key="`footer-3-${requestKey}`"
                  class="border-bottom"
                >
                  <td
                    class="border-left"
                    style="vertical-align: middle !important; text-align: center;"
                  >
                    Remarks
                  </td>
                  <td
                    colspan="4"
                    class="px-1 border-left border-right"
                    style="height: 100px !important; vertical-align: top !important"
                  >
                    {{ request.remarks || 'no remarks' }}
                  </td>
                </tr>

              </tbody>
            </BTableSimple>
          </BCol>
        </BRow>
      </BContainer>
      <template #modal-footer="{ cancel }">
        <div class="flex-grow-1 d-flex justify-content-between mb-0 pb-0 gap-2">
          <div>
            <BButton
              size="sm"
              variant="success"
              tabindex="-1"
              @click.prevent="$event => {
                $event.target.blur()
                cancel()
              }"
            >
              Select More
            </BButton>
          </div>
          <div class="d-flex gap-2">
            <BButton
              size="sm"
              variant="outline-dark"
              tabindex="-1"
              @click.prevent="$event => {
                $event.target.blur()
                cancel()
              }"
            >
              Close <span class="d-none d-md-inline-block">Window</span>
            </BButton>
          </div>
        </div>
      </template>
    </BModal>

  </BContainer>
</template>
<script>
import MISC from '@/mixins/misc'
import FORMATTER from '@/mixins/formatter'

import { SLOReceiving } from '@/services/lo'
import { SSharedList } from '@/services/shared'

export default {
  name: 'VLogisticsOfficerReceivingPending',

  mixins: [
    MISC,
    FORMATTER
  ],

  props: {
    tabIndex: {
      type: Number,
      default: () => 0
    }
  },

  data () {
    return {
      selected: {
        allSelected: false,
        indeterminate: false,
        details: []
      },
      stockRequest: {
        id: 0,
        details: []
      },
      tablePending: {
        busy: false,
        fetching: {
          vehicles: false
        },
        options: {
          tripTypes: [
            { value: 'all', text: 'All' },
            { value: 'Delivery', text: 'Delivery' },
            { value: 'Pick-Up', text: 'Pick-Up' },
            { value: 'Inter Branch', text: 'Inter Branch' }
          ],
          vehicles: []
        },
        selected: {
          allSelected: false,
          indeterminate: false,
          details: [],
          vehicle: ''
        },
        filter: {
          search: '',
          date: '',
          vehicle: '',
          trip_type: 'all'
        },
        filterOn: [],
        stacked: this.isMobile(),
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        rows: [],
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 1, key: 'selected', class: 'text-center' },
          { mobile: 15, key: 'action', class: 'text-center' },
          { mobile: 2, key: 'approvable.created_at', label: 'Ordered At', class: 'min-w-175px', formatter: this.dateTimeShortFormatter, sortable: true },
          { mobile: 3, key: 'approvable.transaction_number', label: 'Transaction Number', class: 'text-center min-w-150px', sortable: true },
          { mobile: 4, key: 'approvable.transfer_number', label: 'Transfer Number', class: 'text-center min-w-150px', sortable: true },
          { mobile: 5, key: 'approvable.schedule_details.trip_type', label: 'Trip Type', class: 'min-w-150px' },
          { mobile: 6, key: 'approvable.schedule_details.date', label: 'Delivery Date', class: 'min-w-175px', formatter: this.dateShortFormatter, sortable: true },
          { mobile: 7, key: 'approvable.schedule_details', label: 'Vehicle', class: 'min-w-325px' },
          { mobile: 8, key: 'approvable.schedule_details.driver_name', label: 'Driver', class: 'min-w-200px' },
          { mobile: 9, key: 'approvable.destination_name', label: 'Deliver To', class: 'min-w-200px' },
          { mobile: 10, key: 'approvable.origin_name', label: 'Delivery From', class: 'min-w-200px' },
          { mobile: 11, key: 'approvable.group_name', label: 'Group', class: 'min-w-200px' },
          { mobile: 12, key: 'approvable.transfer_details.length', label: 'Products', class: 'text-center' },
          { mobile: 13, key: 'approvable.delivery_details.customer', label: 'Customer', class: 'min-w-200px' },
          { mobile: 14, key: 'approvable.customer_direct', label: 'D2C', class: 'text-center', formatter: this.yesOrNo }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  watch: {

    'selected.details' (newValues) {
      if (newValues.length === 0) {
        this.selected.indeterminate = false
        this.selected.allSelected = false
      } else if (newValues.length === this.tablePending.rows.length) {
        this.selected.indeterminate = false
        this.selected.allSelected = true
      } else {
        this.selected.indeterminate = true
        this.selected.allSelected = false
      }
    },

    'tablePending.selected.details' (newValues) {
      if (newValues.length === 0) {
        this.tablePending.selected.indeterminate = false
        this.tablePending.selected.allSelected = false
      } else if (newValues.length === this.stockRequest.details.length) {
        this.tablePending.selected.indeterminate = false
        this.tablePending.selected.allSelected = true
      } else {
        this.tablePending.selected.indeterminate = true
        this.tablePending.selected.allSelected = false
      }
    },

    'tablePending.selected.vehicle' (vehicle) {
      this.tablePending.filter.vehicle = vehicle?.id || ''
    }
  },

  mounted () {
    this.getFilterVehicles()
  },

  methods: {

    async getFilterVehicles () {
      this.tablePending.fetching.vehicles = true
      return await SSharedList.getVehicles().then(({ data }) => {
        this.tablePending.options.vehicles = data
      }).catch(() => {
        this.tablePending.options.vehicles = []
      }).finally(() => {
        this.tablePending.fetching.vehicles = false
      })
    },

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tablePending.busy = true
      return await SLOReceiving.get({
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        filter_delivery_date: filter.date,
        filter_vehicle: filter.vehicle,
        filter_trip_type: filter.trip_type
      }).then(({ data }) => {
        this.tablePending.totalRows = data.total_rows
        this.tablePending.rows = data.items
        return data.items
      }).catch(() => {
        this.tablePending.totalRows = 0
        return []
      }).finally(() => {
        this.tablePending.busy = false
      })
    },

    tableRefresh () {
      this.$refs.tablePending.refresh()
    },

    show (stockRequest) {
      this.$emit('show', stockRequest, true)
    },

    selectPending (selectAll) {
      this.selected.details = selectAll ? this.tablePending.rows.map(
        ({ id }) => Number(id)
      ) : []
    },

    showSelectedPending () {
      this.tablePending.selected.details = []

      const newDetails = this.tablePending.rows.filter(
        ({ id }) => this.selected.details.includes(Number(id))
      ).map(
        ({ approvable, id }) => {
          const additionalRows = Number(approvable.transfer_details.length) >= 8 ? 0 : (
            8 - Number(approvable.transfer_details.length)
          )
          const additionalDetails = Array.from({ length: additionalRows }).map(
            () => ({
              id: '',
              transfer_id: '',
              group_id: approvable.group_id,
              group_code: approvable.group_code,
              group_name: approvable.group_name,
              category_id: '',
              category_code: '',
              category_name: '',
              product_id: '',
              product_code: '',
              product_name: '',
              quantity: '',
              approved_quantity: ''
            })
          )
          const { delivery_details, schedule_details } = approvable
          return ({
            id: id,
            transfer_number: approvable.transfer_number,
            transaction_number: approvable.transaction_number,
            destination_name: approvable.destination_name,
            origin_name: approvable.origin_name,
            customer_direct: approvable.customer_direct,
            customer: delivery_details.customer,
            address: delivery_details.address,
            mobile_number: delivery_details.mobile_number,
            trip_type: schedule_details.trip_type,
            delivery_date: schedule_details.date,
            vehicle_plate: schedule_details.vehicle_plate,
            vehicle_name: schedule_details.vehicle_name,
            driver_number: schedule_details.mobile_number,
            driver_name: schedule_details.driver_name,
            helper: schedule_details.helper,
            details: approvable.transfer_details.map(
              detail => ({
                id: detail.id,
                transfer_id: detail.transfer_id,
                group_id: approvable.group_id,
                group_code: approvable.group_code,
                group_name: approvable.group_name,
                category_id: detail.category_id,
                category_code: detail.category_code,
                category_name: detail.category_name,
                product_id: detail.product_id,
                product_code: detail.product_code,
                product_name: detail.product_name,
                quantity: detail.posting_quantity
              })
            ).concat(additionalDetails),
            requester_remarks: approvable.remarks,
            remarks: this.stockRequest.details.find(
              finder => Number(finder.id) === Number(id)
            )?.remarks || ''
          })
        }
      )

      this.stockRequest.details = newDetails
      this.$bvModal.show('modalReceivingMultiple')
    },

    selectReceivings (selectAll) {
      this.tablePending.selected.details = selectAll ? this.stockRequest.details.map(
        ({ id }) => Number(id)
      ) : []
    },

    removeReceiving (dispatching, _key) {
      const newDetails = this.stockRequest.details.filter(
        ({ id }) => Number(id) !== Number(dispatching.id)
      )

      if (newDetails.length <= 0) {
        return this.swalInvalid('<h6>Receiving must have atleast 1 request</h6>', 'Removal Canceled')
      }

      this.selected.details = this.selected.details.filter(
        id => Number(id) !== Number(dispatching.id)
      )

      this.stockRequest.details = newDetails
    },

    removeReceivings () {
      const newDetails = this.stockRequest.details.filter(
        ({ id }) => !this.tablePending.selected.details.includes(
          Number(id)
        )
      )

      if (newDetails.length <= 0) {
        return this.swalInvalid('<h6>Receiving must have atleast 1 request</h6>', 'Removal Canceled')
      }

      this.selected.details = newDetails.map(
        ({ id }) => Number(id)
      )
      this.stockRequest.details = newDetails
      this.tablePending.selected.details = []
    }
  }
}
</script>
